import { Fragment, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router'
import { Repeat } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Icon } from '../../platform/icons/Icon'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { accountLabel } from '../accounts/accountLabel'
import { useAccounts } from '../accounts/useAccounts'
import { monthName, monthSections, reconnectBillCount, SECTION_ORDER, zeroCurrency } from './listSections'
import { currentOf, readMonth, stripWindow, writeMonth, type YearMonth } from './monthParam'
import { MonthStrip } from './MonthStrip'
import { ReconnectBanner } from './ReconnectBanner'
import { ChoosePaymentSheet } from './ChoosePaymentSheet'
import { AmountChangeShownScope, useRecurringViewed } from './recurringAnalytics'
import { canMarkPaid, localToday, sectionOf, type LocalDate } from './recurringRules'
import type { AccountNames } from './RecurringSections'
import { paymentsLabel, RecurringTiles } from './RecurringTiles'
import { BillRowActions, LifecycleConfirm, type PendingConfirm } from './BillLifecycle'
import type { Bill, Entry } from './recurringWire'
import { useAmountChanges } from './useAmountChanges'
import { useSuggestionCount } from './useSuggestions'
import { liveBill, useBills, useHeaderSummary, useMonth, type HeaderSummary } from './useRecurring'
import { useCurrentOverdue } from './useRecurringOverdueCount'
import { BoardSkeleton, RecurringBoard } from './RecurringBoard'
import { monthKey } from './RecurringCalendar'
import { RECURRING_ALL_PATH, RECURRING_NEW_PATH, RECURRING_PATH, RECURRING_SUGGESTIONS_PATH } from './recurringPaths'
import './recurring.css'

type ListData = { bills: Bill[]; entries: Entry[] }

const LOAD_ERROR = 'Recurring payments could not be loaded.'

/**
 * The bills list decides "truly empty"; the month decides everything else. One
 * error sentence for either failure, and one Try again that retries whichever
 * failed. A stale input passes through as stale so `<Async>` refuses it: this
 * surface is live (P1).
 */
export function combineList(bills: AsyncState<Bill[]>, month: AsyncState<Entry[]>): AsyncState<ListData> {
  if (bills.status === 'error' || month.status === 'error') {
    const recoveries = [bills, month].flatMap((s) => (s.status === 'error' && s.recovery ? [s.recovery] : []))
    return {
      status: 'error',
      message: LOAD_ERROR,
      ...(recoveries.length > 0 ? { recovery: { label: 'Try again', onAct: () => recoveries.forEach((r) => r.onAct()) } } : {}),
    }
  }
  if (bills.status === 'loading' || month.status === 'loading') return { status: 'loading' }
  const billList = bills.status === 'empty' ? [] : bills.data
  if (billList.length === 0) return { status: 'empty' }
  const entries = month.status === 'empty' ? [] : month.data
  const data = { bills: billList, entries }
  if (bills.status === 'stale' || month.status === 'stale') {
    return { status: 'stale', data, asOf: bills.status === 'stale' ? bills.asOf : (month as { asOf: Date }).asOf }
  }
  return { status: 'success', data }
}

function Summary({ bills, header }: { bills: AsyncState<Bill[]>; header: AsyncState<HeaderSummary> }) {
  const known = header.status === 'success' ? header.data.activeCount : bills.status === 'success' ? bills.data.filter((b) => b.isActive).length : null
  if (known === null) {
    return (
      <div className="rec-head__summary" data-testid="rec-summary">
        <Skeleton height={12} width={160} />
      </div>
    )
  }
  return (
    <p className="rec-head__summary" data-testid="rec-summary">
      {known} active
      {header.status === 'success' && (
        <>
          {' · '}
          {header.data.due.length === 0
            ? 'nothing'
            : header.data.due.map((t, i) => (
                <Fragment key={t.currency}>
                  {i > 0 && ' + '}
                  <Money cents={t.cents} currency={t.currency} muted />
                </Fragment>
              ))}
          {` due in ${header.data.monthLabel}`}
        </>
      )}
    </p>
  )
}

function TilesSkeleton({ compact }: { compact: boolean }) {
  return (
    <div className={`rec-tiles${compact ? ' rec-tiles--compact' : ''}`}>
      {[0, 1, 2].map((i) => (
        <Panel key={i} className="rec-tile">
          <Skeleton height={10} width="40%" />
          <Skeleton height={28} width="55%" />
          <Skeleton height={10} width="35%" />
        </Panel>
      ))}
    </div>
  )
}

/**
 * `/recurring/calendar`, kept only so its old links land (#704): the month grid
 * now sits on `/recurring` itself. A REPLACE, so Back does not bounce through
 * it, keeping `?month=` and any other search params. It stays its own `<Route>`
 * so the literal still outranks `:id` (Ruling 41) and "calendar" is never
 * fetched as a bill.
 */
export function RecurringCalendarRedirect() {
  const { search } = useLocation()
  return <Navigate replace to={{ pathname: RECURRING_PATH, search }} />
}

/**
 * `/recurring` — the month (M7-R1–R6, R8, R9, R13, R16, R23, R27; #704
 * supersedes R22 and replaces R24). ONE screen: the header, the strip, the
 * tiles, then the month board — the grid and the list together (see
 * `RecurringBoard`). The frame renders in every state but the truly empty one;
 * only the body below the strip changes, so retrying or switching months never
 * moves the top of the page.
 */
export function Recurring() {
  const today = localToday()
  const [params, setParams] = useSearchParams()
  const selected = readMonth(params, today)
  const current = currentOf(today)
  const breakpoint = useBreakpoint()
  const phone = breakpoint === 'phone'
  // Three tiles share a tablet's narrow content column too, so they compact there as well.
  const compact = phone || breakpoint === 'tablet'
  const navigate = useNavigate()
  // The bill whose Choose-payment sheet is open (a snapshot; `liveBill` reads the current one). One sheet for the page,
  // mounted only while open, so its lock state is the mutation cache's alone.
  const [paying, setPaying] = useState<{ bill: Bill; period: LocalDate | null } | null>(null)
  // The Skip / Delete confirm that is open, one for the page like the sheet.
  const [confirming, setConfirming] = useState<PendingConfirm | null>(null)

  const bills = useBills()
  const month = useMonth(selected)
  const header = useHeaderSummary(today)
  const accountsState = useAccounts()
  const suggestionCount = useSuggestionCount()
  const state = combineList(bills, month)
  // The badge's number, so `recurring_viewed` and the nav agree on what "overdue" meant at the time.
  const overdue = useCurrentOverdue()
  const activeCount =
    state.status === 'success' ? state.data.bills.filter((b) => b.isActive).length : state.status === 'empty' ? 0 : undefined

  const accounts: AccountNames = useMemo(
    () =>
      new Map(
        accountsState.status === 'success' || accountsState.status === 'stale'
          ? accountsState.data.map((a) => [a.id, accountLabel(a)] as const)
          : [],
      ),
    [accountsState],
  )

  // Once per render of new data, shared by the tiles, the sections and the
  // amount-change ids. `entries` is the query's own array, so it is stable
  // while the month is unchanged.
  const entries = state.status === 'success' ? state.data.entries : null
  const sections = useMemo(() => (entries ? monthSections(entries, today) : null), [entries, today])
  const billIds = sections
    ? [...new Set(SECTION_ORDER.flatMap((k) => sections[k].entries.map((e) => e.bill.id)))]
    : []
  const changes = useAmountChanges(billIds)

  const name = monthName(selected)
  const label = `${name} ${selected.year}`
  const empty = state.status === 'empty'

  const select = (ym: YearMonth) => setParams(writeMonth(ym, today))

  useRecurringViewed({ activeCount, overdue })

  const body = (
    <Async
      surface="recurring"
      state={state}
      skeleton={
        <div className="rec-body">
          <TilesSkeleton compact={compact} />
          <BoardSkeleton ym={selected} breakpoint={breakpoint} />
        </div>
      }
      empty={{
        icon: <Icon glyph={Repeat} size={24} />,
        title: 'No recurring payments yet',
        body: "We'll spot repeating charges for you as transactions come in.",
        action: { label: 'Look for recurring payments', onAct: () => navigate(RECURRING_SUGGESTIONS_PATH) },
      }}
      announce={(d) => {
        const n = d.entries.filter((e) => !e.bill.isInflow).length
        return n === 0 ? `${label}: nothing due` : `${label}: ${paymentsLabel(n)}`
      }}
    >
      {(d) => {
        // Non-null here: children only render on success (stale throws, P1).
        const s = sections ?? monthSections(d.entries, today)
        const reconnect = reconnectBillCount(d.entries)
        const actions = (e: Entry) => (
          <BillRowActions
            bill={e.bill}
            entry={e}
            // The design's inline Mark paid: Overdue rows, tablet and up, and
            // only the period the server pays (Ruling 34). On a phone the
            // row's menu carries it.
            inline={!phone && sectionOf(e, today) === 'overdue' && canMarkPaid(e.bill, e)}
            onOpenSheet={(_, period) => setPaying({ bill: e.bill, period })}
            onConfirm={setConfirming}
          />
        )
        return (
          <div className="rec-body">
            {reconnect > 0 && (
              <ReconnectBanner
                text={reconnect === 1 ? "1 bill can't be confirmed." : `${reconnect} bills can't be confirmed.`}
              />
            )}
            <RecurringTiles
              sections={s}
              today={today}
              monthName={name}
              zeroCurrency={zeroCurrency(d.entries, d.bills)}
              compact={compact}
            />
            <RecurringBoard
              key={monthKey(selected)}
              ym={selected}
              sections={s}
              today={today}
              breakpoint={breakpoint}
              accounts={accounts}
              changes={changes}
              actions={actions}
            />
          </div>
        )
      }}
    </Async>
  )

  return (
    <AmountChangeShownScope>
      <div className="rec-page">
        <div className="rec-head">
          <div className="rec-head__identity">
            {!empty && <Summary bills={bills} header={header} />}
          </div>
          <div className="rec-head__actions">
            {/* On a phone the actions wrap; the primary comes first in the DOM
                there so it leads both the reading order and the first row. */}
            {phone && (
              <ButtonLink to={RECURRING_NEW_PATH} variant="primary" size="page">
                + Add recurring
              </ButtonLink>
            )}
            {!empty && (
              <>
                <ButtonLink to={RECURRING_SUGGESTIONS_PATH} variant="outline" size="page">
                  {/* One text node: the button is a flex row, which drops a bare space between two children. */}
                  {suggestionCount ? `Suggestions (${suggestionCount})` : 'Suggestions'}
                </ButtonLink>
                <ButtonLink to={RECURRING_ALL_PATH} variant="outline" size="page">
                  All recurring
                </ButtonLink>
              </>
            )}
            {!phone && (
              <ButtonLink to={RECURRING_NEW_PATH} variant="primary" size="page">
                + Add recurring
              </ButtonLink>
            )}
          </div>
        </div>
        {!empty && (
          <MonthStrip months={stripWindow(today)} selected={selected} currentYear={current.year} onSelect={select} />
        )}
        {empty ? <Panel className="rec-empty">{body}</Panel> : body}
        {paying && (
          <ChoosePaymentSheet
            bill={liveBill(paying.bill, bills)}
            period={paying.period}
            onClose={() => setPaying(null)}
          />
        )}
        {confirming && <LifecycleConfirm confirm={confirming} onClose={() => setConfirming(null)} />}
      </div>
    </AmountChangeShownScope>
  )
}
